// Keyboard navigation shared by ALL dropdowns in the system (chips in `ChipSelect` and
// `ListChipCell`, the Grid `dropdown` feature, address autocomplete, client search):
// as soon as it opens or text is typed, the first option is highlighted, ↑/↓ arrows move
// the selection, Enter or Tab selects the active option, and Escape closes.
// Mouse clicks continue to work as usual without passing through here.
//
// Each dropdown builds its own list of navigable options (some start with an "empty value"
// option, others do not) and this hook tracks the highlighted index.

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefCallback } from 'react'

export interface DropdownKeyboardConfig {
  /** Count of navigable options. */
  count?: number
  /** Whether the dropdown is open. When `false`, Enter/Tab do not make a selection. */
  open?: boolean
  /** Option index highlighted upon opening and whenever `resetWith` changes. */
  initialIndex?: number
  /** When this value changes (typically search query), resets to `initialIndex`. */
  resetWith?: unknown
  /** Confirms the highlighted option (Enter or Tab). */
  onSelect?: (index: number) => void
  /** Escape key handler. If omitted, Escape does nothing. */
  onClose?: () => void
  /** If defined, pressing ↓ with the dropdown closed opens it instead of moving highlight. */
  onOpen?: () => void

  // Backward compatibility properties
  cantidad?: number
  abierto?: boolean
  indiceInicial?: number
  reiniciarCon?: unknown
  elegir?: (indice: number) => void
  cerrar?: () => void
  abrir?: () => void
}

export type ConfigTecladoDesplegable = DropdownKeyboardConfig

export interface DropdownKeyboardResult<E extends HTMLElement> {
  /** Highlighted index. */
  index: number
  setIndex: (index: number) => void
  /** Assigned to the highlighted option (`ref={i === index ? activeRef : undefined}`): auto-scrolls to it. */
  activeRef: RefCallback<E>
  /** `onKeyDown` handler for the input (or container if dropdown has no search input). */
  onKeyDown: (e: KeyboardEvent) => void

  // Backward compatibility properties
  indice: number
  setIndice: (indice: number) => void
  refActiva: RefCallback<E>
  alTeclear: (e: KeyboardEvent) => void
}

export type TecladoDesplegable<E extends HTMLElement> = DropdownKeyboardResult<E>

export function useDropdownKeyboard<E extends HTMLElement = HTMLButtonElement>(
  config: DropdownKeyboardConfig,
): DropdownKeyboardResult<E> {
  const count = config.count ?? config.cantidad ?? 0
  const open = config.open ?? config.abierto ?? true
  const initialIndex = config.initialIndex ?? config.indiceInicial ?? 0
  const resetWith = config.resetWith !== undefined ? config.resetWith : config.reiniciarCon
  const onSelect = config.onSelect ?? config.elegir ?? (() => {})
  const onClose = config.onClose ?? config.cerrar
  const onOpen = config.onOpen ?? config.abrir

  const [index, setIndex] = useState(initialIndex)
  const activeNode = useRef<E | null>(null)
  const activeRef = useCallback<RefCallback<E>>((el) => { activeNode.current = el }, [])

  // On open, on search query change, or on options update (which often load asynchronously),
  // reset to the initial option so Enter always confirms the closest search match.
  useEffect(() => { setIndex(initialIndex) }, [resetWith, initialIndex, count, open])

  // Scroll list into view when navigating options via arrow keys.
  useEffect(() => { activeNode.current?.scrollIntoView({ block: 'nearest' }) }, [index, open])

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (onOpen && !open) { onOpen(); return }
      setIndex((i) => Math.min(i + 1, count - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) return
      setIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // Do not capture key if dropdown is closed or empty: Tab continues focus, Enter reaches form.
      if (!open || count === 0) return
      e.preventDefault()
      onSelect(index)
    } else if (e.key === 'Escape') {
      if (!onClose) return
      e.preventDefault()
      onClose()
    }
  }, [open, count, index, onSelect, onClose, onOpen])

  return {
    index,
    setIndex,
    activeRef,
    onKeyDown,
    // Backward compatibility
    indice: index,
    setIndice: setIndex,
    refActiva: activeRef,
    alTeclear: onKeyDown,
  }
}

export const useTecladoDesplegable = useDropdownKeyboard

